import { Fragment, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '../../../../componentsv2/Typography';
import Button from '../../../../componentsv2/Button';
import { TextField } from '../../../../componentsv2/Field';
import ErrorMessage from '../../../../components/ErrorMessage';
import { useTranslation } from '../../../../hooks/i18n';
import { Nullable } from '../../../../types';
import { COLORS } from '../../../../themev2';

interface Step3RecoveryCodeProps {
  onBack: () => void;
  onSubmit: (code: string) => Promise<void>;
  error: Nullable<string>;
  setError: (error: Nullable<string>) => void;
  isLoading: boolean;
}

const Step3RecoveryCode = ({ onBack, onSubmit, error, setError, isLoading }: Step3RecoveryCodeProps) => {
  const { t } = useTranslation();
  const [recoveryCode, setRecoveryCode] = useState('');

  const handleSubmit = async () => {
    if (!recoveryCode.trim()) {
      setError(t('common:twoFactor.invalidRecoveryCode'));
      return;
    }

    await onSubmit(recoveryCode.trim());
  };

  return (
    <Fragment>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {t('common:twoFactor.recoveryCodeTitle')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: COLORS.gray[1100],
              size: '18px',
              fontWeight: 400,
            }}
          >
            {t('common:twoFactor.recoveryCodeDescription')}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ marginTop: '2rem' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: { xs: '100%', lg: '60%' },
              margin: '0 auto',
            }}
          >
            <TextField
              fullWidth
              name="recoveryCode"
              placeholder={t('common:twoFactor.recoveryCodePlaceholder')}
              value={recoveryCode}
              onChange={(value) => {
                setRecoveryCode(value);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ marginTop: '2rem' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: { xs: '100%', lg: '60%' },
              margin: '0 auto',
            }}
          >
            <Grid container rowSpacing={2}>
              <Grid item xs={6} md={3}>
                <Button fullWidth type="outline" color="tertiary" onClick={onBack}>
                  {t('common:back')}
                </Button>
              </Grid>
              <Grid item xs={6} md={9}>
                <Button
                  fullWidth
                  color="secondary"
                  isLoading={isLoading}
                  onClick={handleSubmit}
                  disabled={!recoveryCode.trim()}
                >
                  {t('common:twoFactor.continueButton')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        {error && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: { xs: '100%', md: '50%', lg: '40%' },
              }}
            >
              <ErrorMessage
                errorMessage={error}
                onDismiss={() => {
                  setError(null);
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

export default Step3RecoveryCode;
